import React, { useContext } from "react";
import { StyleSheet, Text, View, TouchableOpacity } from "react-native";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { AuthContext } from "../../contexts/auth/Store";
import useFetch from "../../hooks/useFetch";
import useOrderTotal from "../../hooks/useOrderTotal";
import { ILedger, LedgerHead, LedgerSubhead } from "../../interfaces/ledger";
import { IOrderGroup } from "../../interfaces/order";
import { ledgerService } from "../../services/LedgerService";
import { formatCurrency } from "../../utils/money";
import Divider from "./Divider";

interface IProp {
  order_group: IOrderGroup;
  isCart: any;
}

const DeliverySummary: React.FC<IProp> = ({ order_group, isCart }) => {
  const auth = useContext(AuthContext);
  const buyer = auth.state.buyerData;

  const [collapsed, setCollapsed]: [boolean, any] = React.useState(true);

  const { total, subtotal, charges, totalCharge } = useOrderTotal(
    order_group,
    isCart
  );

  const toggleCollapse = () => {
    setCollapsed((prev: boolean) => !prev);
  };

  const [ledger] = useFetch<ILedger>(
    ledgerService,
    "fetchByOwnerSpecific",
    [
      {
        head: LedgerHead.Buyer,
        subhead: LedgerSubhead.Wallet,
        owner: buyer?._id,
      },
    ],
    // !!buyer?._id
    false
  );

  return (
    <>
      <View style={styles.summaryCard}>
        <View style={styles.summaryContainer}>
          <Text style={styles.lineItemText}>Subtotal</Text>
          <Text style={styles.lineItemText}>{formatCurrency(subtotal)}</Text>
        </View>
        {total && totalCharge?.value ? (
          <>
            <View style={styles.summaryContainer}>
              <Text style={styles.lineItemText}>{totalCharge.title}</Text>
              <Text style={styles.lineItemText}>
                {formatCurrency(totalCharge.value)}
              </Text>
            </View>
            {charges.length > 1 ? (
              !collapsed ? (
                <>
                  {charges.map((charge) => (
                    <View key={charge._id} style={styles.summaryContainer}>
                      <Text style={styles.chargesTitle}>+ {charge.title}</Text>
                      <Text style={styles.chargesValue}>
                        {formatCurrency(charge.value, true)}
                      </Text>
                    </View>
                  ))}
                </>
              ) : (
                <TouchableOpacity
                  style={styles.summaryContainer}
                  onPress={toggleCollapse}
                >
                  <Text
                    style={{
                      ...FontStyles.default,
                      ...FontStyles.size13,
                      ...FontStyles.lh16,
                      ...FontStyles.normal,
                    }}
                  >
                    Show breakdown
                  </Text>
                </TouchableOpacity>
              )
            ) : null}
          </>
        ) : null}
        {(isCart || order_group?.is_latest) && ledger?.balance !== 0 && false ? (
          <>
            <Divider margin={10} />

            {total && (
              <View style={[styles.summaryContainer]}>
                <Text style={styles.subtotalText}>Invoice Total</Text>

                <Text style={styles.subtotalText}>{formatCurrency(total)}</Text>
              </View>
            )}

            <View style={styles.summaryContainer}>
              <Text style={styles.lineItemText}>
                {ledger?.balance < 0 ? "Outstanding" : "Balance"}
              </Text>
              <Text
                style={[
                  styles.lineItemText,
                  ledger?.balance < 0
                    ? styles.lineItemTextCritical
                    : styles.lineItemTextSuccess,
                ]}
              >
                {formatCurrency(Math.abs(ledger?.balance || 0))}
              </Text>
            </View>

            <Divider margin={10} />

            <View style={[styles.summaryContainer]}>
              <Text style={styles.totalTitle}>Amount Due</Text>
              {total ? (
                <Text style={styles.totalValue}>
                  {formatCurrency((ledger?.balance || 0) * -1 + total)}
                </Text>
              ) : (
                <Text style={styles.totalValue}>
                  {formatCurrency((ledger?.balance || 0) * -1)}
                </Text>
              )}
            </View>
          </>
        ) : (
          <>
            <Divider margin={10} />

            <View style={[styles.summaryContainer]}>
              <Text style={styles.totalTitle}>Amount Due</Text>
              {total ? (
                <Text style={styles.totalValue}>{formatCurrency(total)}</Text>
              ) : null}
            </View>
          </>
        )}
      </View>
    </>
  );
};

export default DeliverySummary;

const styles = StyleSheet.create({
  summaryCard: {
    marginTop: 16,
    paddingHorizontal: 16,
  },
  summaryContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    paddingTop: 8,
  },
  lineItemText: {
    ...FontStyles.size14,
    ...FontStyles.lh18,
    ...FontStyles.normal,
    ...FontStyles.dark,
  },
  lineItemTextCritical: {
    color: Colors.dangerText,
  },
  lineItemTextSuccess: {
    color: Colors.successText,
  },
  totalTitle: {
    ...FontStyles.size14,
    ...FontStyles.lh18,
    ...FontStyles.semibold,
    ...FontStyles.dark,
  },
  totalValue: {
    ...FontStyles.size18,
    ...FontStyles.lh22,
    ...FontStyles.semibold,
    ...FontStyles.dark,
  },
  subtotalText: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.semibold,
    ...FontStyles.dark,
  },
  chargesTitle: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    ...FontStyles.gray,
  },
  chargesValue: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    ...FontStyles.gray,
  },
});
